<template>
  <div class="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
    <div class="w-full max-w-md">
      <!-- Success State -->
      <div 
        v-if="isSuccess" 
        class="relative bg-gradient-to-br from-white to-slate-50/30 border border-gray-200 rounded-2xl px-8 py-10 transition-all duration-300 shadow-2xl hover:-translate-y-1 group overflow-hidden"
      >
        <!-- Decorative background element -->
        <div class="absolute -top-8 -right-8 w-24 h-24 opacity-5 group-hover:opacity-10 transition-opacity duration-500">
          <div class="w-full h-full bg-gradient-to-br from-emerald-400 to-blue-500 rounded-2xl transform rotate-45"></div>
        </div>

        <div class="relative z-10 flex flex-col items-center">
          <!-- Success icon with enhanced styling -->
          <div class="relative mb-6">
            <div class="w-20 h-20 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <div class="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-xl flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
                <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <!-- Animated underline -->
            <div class="w-0 h-1 bg-gradient-to-r from-emerald-400 to-emerald-500 group-hover:w-20 transition-all duration-700 mt-2 mx-auto"></div>
          </div>

          <!-- Title with gradient text -->
          <h2 class="text-3xl font-bold text-gray-900 group-hover:text-emerald-600 transition-colors duration-300 mb-3 text-center leading-tight">
            Paiement Réussi !
          </h2>

          <!-- Success badge -->
          <div class="inline-flex items-center space-x-2 bg-gradient-to-r from-emerald-50 to-emerald-50 group-hover:from-emerald-100 group-hover:to-emerald-100 px-4 py-2 rounded-xl transition-all duration-300 group-hover:scale-105 mb-6">
            <div class="w-6 h-6 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-lg flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
              <i class="fa-solid fa-check text-white text-xs"></i>
            </div>
            <span class="text-sm font-semibold text-emerald-700 group-hover:text-emerald-800 transition-colors">
              Transaction Terminée
            </span>
          </div>

          <!-- Description with accent bar -->
          <div class="relative mb-8">
            <div class="absolute -left-2 top-1 w-1 h-4 bg-gradient-to-b from-emerald-400 to-emerald-500 rounded-full opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
            <p class="text-gray-600 text-center leading-relaxed group-hover:text-gray-700 transition-colors duration-300 pl-4">
              Votre achat a été effectué avec succès. Votre forfait est en cours d'activation et votre compte sera mis à jour dans quelques minutes.
            </p>
          </div>

          <!-- Enhanced button -->
          <router-link to="/dashboard" class="w-full">
            <button class="relative w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl font-semibold py-4 px-6 transition-all duration-300 transform hover:scale-105 hover:shadow-xl overflow-hidden group">
              <span class="relative z-10 flex items-center justify-center space-x-2">
                <span>Aller au Tableau de Bord</span>
                <i class="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform duration-300"></i>
              </span>
              <!-- Button shimmer effect -->
              <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out"></div>
            </button>
          </router-link>
        </div>

        <!-- Animated dots -->
        <div class="absolute bottom-4 right-4 opacity-0 group-hover:opacity-30 transition-opacity duration-500">
          <div class="flex space-x-1">
            <div class="w-1 h-1 bg-emerald-500 rounded-full animate-bounce" style="animation-delay: 0ms;"></div>
            <div class="w-1 h-1 bg-emerald-300 rounded-full animate-bounce" style="animation-delay: 150ms;"></div>
            <div class="w-1 h-1 bg-emerald-100 rounded-full animate-bounce" style="animation-delay: 300ms;"></div>
          </div>
        </div>

        <!-- Main shimmer effect -->
        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out"></div>
      </div>

      <!-- Error State -->
      <div 
        v-else 
        class="relative bg-gradient-to-br from-white to-slate-50/30 border border-gray-200 rounded-2xl px-8 py-10 transition-all duration-300 shadow-2xl hover:-translate-y-1 group overflow-hidden"
      >
        <!-- Decorative background element -->
        <div class="absolute -top-8 -right-8 w-24 h-24 opacity-5 group-hover:opacity-10 transition-opacity duration-500">
          <div class="w-full h-full bg-gradient-to-br from-red-400 to-orange-500 rounded-2xl transform rotate-45"></div>
        </div>

        <div class="relative z-10 flex flex-col items-center">
          <!-- Error icon with enhanced styling -->
          <div class="relative mb-6">
            <div class="w-20 h-20 bg-gradient-to-br from-red-100 to-red-50 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <div class="w-12 h-12 bg-gradient-to-br from-red-400 to-red-500 rounded-xl flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
                <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <!-- Animated underline -->
            <div class="w-0 h-1 bg-gradient-to-r from-red-400 to-red-500 group-hover:w-20 transition-all duration-700 mt-2 mx-auto"></div>
          </div>

          <!-- Title with gradient text -->
          <h2 class="text-3xl font-bold text-gray-900 group-hover:text-red-600 transition-colors duration-300 mb-3 text-center leading-tight">
            Erreur de Paiement
          </h2>

          <!-- Error badge -->
          <div class="inline-flex items-center space-x-2 bg-gradient-to-r from-red-50 to-red-50 group-hover:from-red-100 group-hover:to-red-100 px-4 py-2 rounded-xl transition-all duration-300 group-hover:scale-105 mb-6">
            <div class="w-6 h-6 bg-gradient-to-br from-red-400 to-red-500 rounded-lg flex items-center justify-center group-hover:rotate-12 transition-transform duration-300">
              <i class="fa-solid fa-exclamation text-white text-xs"></i>
            </div>
            <span class="text-sm font-semibold text-red-700 group-hover:text-red-800 transition-colors">
              Transaction Échouée
            </span>
          </div>

          <!-- Description with accent bar -->
          <div class="relative mb-8">
            <div class="absolute -left-2 top-1 w-1 h-4 bg-gradient-to-b from-red-400 to-red-500 rounded-full opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
            <p class="text-gray-600 text-center leading-relaxed group-hover:text-gray-700 transition-colors duration-300 pl-4">
              Un problème s'est produit lors du traitement de votre paiement. Veuillez réessayer ou contacter le support.
            </p>
          </div>

          <!-- Enhanced button -->
          <router-link to="/packages" class="w-full">
            <button class="relative w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-semibold py-4 px-6 transition-all duration-300 transform hover:scale-105 hover:shadow-xl overflow-hidden group">
              <span class="relative z-10 flex items-center justify-center space-x-2">
                <span>Réessayer</span>
                <i class="fa-solid fa-arrow-right group-hover:translate-x-1 transition-transform duration-300"></i>
              </span>
              <!-- Button shimmer effect -->
              <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out"></div>
            </button>
          </router-link>
        </div>

        <!-- Animated dots -->
        <div class="absolute bottom-4 right-4 opacity-0 group-hover:opacity-30 transition-opacity duration-500">
          <div class="flex space-x-1">
            <div class="w-1 h-1 bg-red-500 rounded-full animate-bounce" style="animation-delay: 0ms;"></div>
            <div class="w-1 h-1 bg-red-300 rounded-full animate-bounce" style="animation-delay: 150ms;"></div>
            <div class="w-1 h-1 bg-red-100 rounded-full animate-bounce" style="animation-delay: 300ms;"></div>
          </div>
        </div>

        <!-- Main shimmer effect -->
        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const isSuccess = computed(() => route.path === '/checkout/success')
</script>